import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useScrollReveal from '../hooks/useScrollReveal';
import styles from '../styles/ContactPage.module.css';
import ReCAPTCHA from "react-google-recaptcha";
import Seo from '../components/seo';


<Seo 
        title="Contact Us | Book a B2B Outreach Strategy Call | InviteEngine" 
        description="Ready to grow your pipeline? Contact InviteEngine to book a free B2B outreach strategy call. We help SMBs start meaningful sales conversations."
        path="/contact" 
      >
        {/* ── SCHEMA MARKUP ── */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "mainEntity": {
              "@type": "Organization",
              "name": "InviteEngine",
              "areaServed": "Worldwide",
              "description": "B2B outreach specialists helping worldwide SMBs build authentic sales pipelines."
            }
          })}
        </script>
      </Seo>

const SERVICES = [
  'Inbox to Invite',
  'Dials to Discovery',
  'Full Spectrum Outreach',
  'Reach at Scale',
  'Outcome-Driven Outreach',
  "I'm not sure yet",
];

export default function ContactPage() {
  useScrollReveal();
  
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [form, setForm] = useState({
    name: '', email: '', company: '', phone: '', service: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [captchaValue, setCaptchaValue] = useState(null);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    
    if (!form.email.trim()) {
      e.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = 'Enter a valid email';
    }

    // --- STRICT PHONE REQUIREMENT ---
    if (!form.phone.trim()) {
      e.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-]{10,}$/.test(form.phone)) {
      e.phone = 'Please enter a valid phone number (min 10 digits)';
    }

    if (!form.company.trim()) e.company = 'Company is required';
    if (!form.message.trim()) e.message = 'Tell us a bit about your needs';
    
    if (!captchaValue) {
      alert("Please complete the reCAPTCHA");
      return { captcha: 'required' };
    }

    return e;
  };

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setErrors((er) => ({ ...er, [e.target.name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    
    if (Object.keys(e2).length > 0) { 
      setErrors(e2); 
      return; 
    }

    // --- DIRECT EMAIL SUBMISSION LOGIC ---
    try {
      const response = await fetch("https://formsubmit.co/ajax/connect@inviteengine.com", {
        method: "POST",
        headers: { 
            "Content-Type": "application/json",
            "Accept": "application/json" 
        },
        body: JSON.stringify({
            Name: form.name,
            Email: form.email,
            Company: form.company,
            Phone: form.phone,
            Service: form.service,
            Message: form.message,
            "_subject": `New Inquiry from ${form.name}`,
            "_captcha": "false" // We are using our own reCAPTCHA
        })
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        alert("Submission failed. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Something went wrong.");
    }
  };

  const onCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  return (

    <main className={styles.page}>
      {/* ── PAGE HERO ── */}
      <section className={styles.pageHero}>
        <div className={styles.pageHeroBg} />
        <div className="container" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <span className={styles.breadcrumb}><Link to="/">Home</Link> / Contact Us</span>
          <h1 className={styles.pageTitle}>Let's Start the Conversation</h1>
          <p className={styles.pageSubtitle}>
            Tell us about your goals and we'll design an outreach strategy tailored to your pipeline.
          </p>
        </div>
      </section>

      {/* ── MAIN CONTENT ── */}
      <section className={styles.contactSection}>
        <div className="container">
          <div className="row g-5 align-items-start">

            <div className="col-lg-4">
              <div className={`${styles.infoBlock} reveal`}>
                <h3 className={styles.infoTitle}>Get in Touch</h3>
                {[
                  { icon: '📧', label: 'Email Us', value: 'connect@inviteengine.com', href: 'mailto:connect@inviteengine.com' },
                  { icon: '📞', label: 'Call Us',  value: '+91 8712456131', href: 'tel:+91 8712456131' },
                  { icon: '📍', label: 'Location', value: 'Hyderabad, Telangana, India', href: null },
                  { icon: '⏰', label: 'Hours',    value: 'Mon–Fri· 9am–6pm IST', href: null },
                ].map((c) => (
                  <div className={styles.contactDetail} key={c.label}>
                    <span className={styles.contactIcon}>{c.icon}</span>
                    <div>
                      <span className={styles.contactLabel}>{c.label}</span>
                      {c.href
                        ? <a href={c.href} className={styles.contactValue}>{c.value}</a>
                        : <span className={styles.contactValue}>{c.value}</span>
                      }
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="col-lg-8">
              {submitted ? (
                <div className={`${styles.successBox} reveal`}>
                  <span className={styles.successEmoji}>🎉</span>
                  <h2>Message Received!</h2>
                  <p>Thanks for reaching out. A member of our team will reply within 2 hours...</p>
                  <Link to="/" className="btn-primary-ie" style={{ marginTop: '1.5rem' }}>Back to Home</Link>
                </div>
              ) : (
                <div className={`${styles.formCard} reveal`}>
                  <h2 className={styles.formTitle}>Book Your Free Strategy Call</h2>
                  <form onSubmit={handleSubmit} noValidate>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className={styles.label}>Full Name *</label>
                        <input name="name" type="text" className={`${styles.input} ${errors.name ? styles.inputError : ''}`} value={form.name} onChange={handleChange} />
                        {errors.name && <span className={styles.errorMsg}>{errors.name}</span>}
                      </div>

                      <div className="col-md-6">
                        <label className={styles.label}>Work Email *</label>
                        <input name="email" type="email" className={`${styles.input} ${errors.email ? styles.inputError : ''}`} value={form.email} onChange={handleChange} />
                        {errors.email && <span className={styles.errorMsg}>{errors.email}</span>}
                      </div>

                      <div className="col-md-6">
                        <label className={styles.label}>Company Name *</label>
                        <input name="company" type="text" className={`${styles.input} ${errors.company ? styles.inputError : ''}`} value={form.company} onChange={handleChange} />
                        {errors.company && <span className={styles.errorMsg}>{errors.company}</span>}
                      </div>

                      <div className="col-md-6">
                        <label className={styles.label}>Phone Number *</label>
                        <input name="phone" type="tel" className={`${styles.input} ${errors.phone ? styles.inputError : ''}`} value={form.phone} onChange={handleChange} />
                        {errors.phone && <span className={styles.errorMsg}>{errors.phone}</span>}
                      </div>

                      <div className="col-md-12">
                        <label className={styles.label}>Service Interested In</label>
                        <select name="service" className={styles.select} value={form.service} onChange={handleChange}>
                          <option value="">Select a service…</option>
                          {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>

                      <div className="col-12">
                        <label className={styles.label}>Tell Us About Your Goals *</label>
                        <textarea name="message" rows={5} className={`${styles.textarea} ${errors.message ? styles.inputError : ''}`} value={form.message} onChange={handleChange} />
                        {errors.message && <span className={styles.errorMsg}>{errors.message}</span>}
                      </div>

                      <div className="col-12">
                        <ReCAPTCHA sitekey="6LcxHnQsAAAAAFpbPXlzEphQ6ROfGCJlQrGmA3s4" onChange={onCaptchaChange} />
                      </div>

                      <div className="col-12">
                        <button type="submit" className={`${styles.submitBtn} btn-primary-ie`}>Submit</button>
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );



}